import React, { Component } from "react";
import { connect } from "react-redux";
import { Route, Switch, withRouter } from "react-router-dom";
import { loadUserFromToken } from "./features/user/actions";

import PrivateRoute from "./utils/PrivateRoute";
import LoginScreen from "./features/home/containers/LoginScreen";
import SessionExpiredScreen from "./features/home/containers/SessionExpiredScreen";
import LogoutScreen from "./features/home/containers/LogoutScreen";
import Loading from "./features/shared/components/misc/Loading";
import PageNotFound from "./features/shared/components/notices/PageNotFound";
import Segment from "./features/shared/containers/Segment";

import ErrorBoundary from "./features/shared/components/layouts/ErrorBoundary";
import PreKYCScreen from './features/home/containers/PreKYCScreen';
import Requirements from "./features/home/containers/Requirements";
import ForgotPasswordContainer from "./features/user/containers/ForgotPassword";
import EmailSentContainer from "./features/user/containers/EmailSent";
import ResetPasswordContainer from "./features/user/containers/ResetPasswordContainer";
import UserProfile from "./features/user/containers/UserProfile";
import ConfirmEmail from "./features/user/containers/ConfirmEmail";

// home
import HomeContainer from "./features/home/containers/Home";

// account
import AccountRoutes from "./features/account/routes";

// features folder
import CreditLimitRoutes from './features/CreditLimit/routes';

// company
import CompanyInformation from "./features/company/containers/CompanyInformation";

// loans
import DrawDown from './features/loans/containers/DrawDown';
import ReviewDrawDownDetails from './features/loans/containers/ReviewDrawDownDetails';
import LoanPlans from "./features/loans/containers/LoanPlans";
import LoanPlanOverview from "./features/loans/containers/LoanPlanOverview";
import RCLRequestReceivedInstruction from "./features/loans/containers/RCLRequestReceivedInstruction";
import FCPaymentCompleted from "./features/loans/containers/FCPaymentCompleted";

// trade documents
import purchaseOrderRoutes from "./features/trade_documents/purchaseOrderRoutes";
import invoiceRoutes from "./features/trade_documents/invoiceRoutes";
import supportingDocumentRoutes from "./features/supporting_documents/routes";

// apply now
import ApplyNow from './features/applyNow/containers/ApplyNow';

// trustpass
import PersonalTrustpassRoutes from "./features/trustpass/ptpRoutes";
import BusinessTrustpassRoutes from "./features/trustpass/btpRoutes";

// applications
import Applications from "./features/trade_documents/components/Applications";

import notifications from "./features/notifications/routes";

import { shouldNotBeOnboarded } from './features/home/misc/CustomerStatus'
import { setRestrictedAccessTokenCookie, urlIsRestrictedAccess, decodedJwt, hasJwtTokenSet } from './utils/cookies';
import { dontFetchInThisPath } from "./utils/url";
import withDesignSystemLoader from "./utils/withDesignSystemLoader";
import CompanyLoader from "./features/shared/components/loaders/CompanyLoader";

// Project Finder
import ProjectFinder from "./features/projectFinder/containers/ProjectFinder";
import ProjectFinderLandingPage from "./features/projectFinderLandingPage/containers/ProjectFinderLandingPage";
import ProjectFinderAlerts from "./features/alertsManager/containers/ProjectFinderAlerts"

class RootRoute extends Component {
  componentWillMount() {
    // NOTE: We will allow internal
    // access for the moment. Make sure to come back on this.
    if (urlIsRestrictedAccess()) {
      setRestrictedAccessTokenCookie();
    }

    // Only fetch user from token if path is allowed and token is present
    if (hasJwtTokenSet() && !dontFetchInThisPath(window.location.pathname)) {
      this.props.dispatch(loadUserFromToken())
    }
  }

  authenticatedRootComponent = () => {
    const { user: customer } = this.props;
    if (shouldNotBeOnboarded(customer)) {
      return HomeContainer;
    } else {
      return PreKYCScreen;
    }
  }

  renderFullRoutes = () => {
    return (
      <Switch>
        <Route exact path='/login' component={LoginScreen} />
        <Route exact path='/logout' component={LogoutScreen} />
        <Route exact path='/session_expired' component={SessionExpiredScreen} />
        <Route
          exact
          path='/apply'
          render={() =>
          (window.location =
            process.env.REACT_APP_ONBOARDING_DOMAIN_API)
          }
        />
        <Route exact path='/loading' component={Loading} />
        <Route
          exact
          path='/forgot_password'
          component={ForgotPasswordContainer}
        />
        <Route path='/email_sent' component={EmailSentContainer} />
        <Route path='/reset_password' component={ResetPasswordContainer} />
        <PrivateRoute exact path='/account' component={AccountRoutes} />
        <PrivateRoute
          exact
          path='/'
          component={this.authenticatedRootComponent()}
        />
        <PrivateRoute exact path='/requirements' component={Requirements} />
        <PrivateRoute
          path='/company_information'
          component={CompanyInformation}
        />
        <PrivateRoute
          path='/(personal_trustpasses|personal_trustpass)'
          component={PersonalTrustpassRoutes}
        />
        <PrivateRoute
          path='/(business_trustpasses|business_trustpass)'
          component={BusinessTrustpassRoutes}
        />
        <PrivateRoute path='/loan_plans' component={LoanPlans} />
        <PrivateRoute
          path='/loan_plan_overview/:loan_plan_id'
          component={LoanPlanOverview}
        />

        <PrivateRoute exact path='/draw_down' component={DrawDown} />

        <PrivateRoute
          exact
          path='/draw_down/review'
          component={ReviewDrawDownDetails}
        />
        <PrivateRoute
          exact
          path='/draw_down/requested'
          component={RCLRequestReceivedInstruction}
        />
        <PrivateRoute
          exact
          path='/draw_down/completed'
          component={FCPaymentCompleted}
        />
        <PrivateRoute path='/user_profile' component={UserProfile} />
        <PrivateRoute
          path='/confirm_email/:token'
          component={ConfirmEmail}
        />
        <PrivateRoute
          path='/credit-limit/intro'
          component={CreditLimitRoutes}
        />
        <PrivateRoute
          path='/(purchase_orders|invoices)/:id/supporting_documents'
          component={supportingDocumentRoutes}
        />
        <PrivateRoute
          path='/purchase_orders'
          component={purchaseOrderRoutes}
        />
        <PrivateRoute path='/invoices' component={invoiceRoutes} />
        <PrivateRoute
          exact
          path='/notifications'
          component={notifications}
        />
        <PrivateRoute exact path='/applications' component={Applications} />
        <PrivateRoute exact path='/apply-now' component={ApplyNow} />
        <Route exact path='/project-finder/search' component={ProjectFinder} />
        <Route exact path='/project-finder' component={ProjectFinderLandingPage} />
        <PrivateRoute exact path='/manage-alerts' component={ProjectFinderAlerts} />
        <Route component={PageNotFound} />
      </Switch>
    )
  }

  renderLimitedRoutes = () => {
    return (
      <Switch>
        <PrivateRoute
          exact
          path='/'
          component={this.authenticatedRootComponent()}
        />
        <PrivateRoute exact path='/draw_down' component={DrawDown} />
          <PrivateRoute
            exact
            path='/draw_down/review'
            component={ReviewDrawDownDetails}
          />
          <PrivateRoute
            exact
            path='/draw_down/requested'
            component={RCLRequestReceivedInstruction}
          />
          <PrivateRoute
            exact
            path='/draw_down/completed'
            component={FCPaymentCompleted}
          />
        <Route component={PageNotFound} />
      </Switch>
    )
  }

  renderRoutes = () => {
    var decoded = decodedJwt();

    // Note: To handle undefined jwt token cookie.
    // Adjust, improve this in next iterations.
    if (decoded && decoded.scope === 'draw_down') {
      return this.renderLimitedRoutes()
    } else {
      return this.renderFullRoutes()
    }
  }

  render() {
    if (this.props.dsLoaded) {
      return (
        <main>
          <Segment />
          <ErrorBoundary>
            {this.renderRoutes()}
          </ErrorBoundary>
        </main>
      );
    } else {
      return <CompanyLoader />
    }
  }
}

const mapStateToProps = (state) => {
  return {
    user: state.user,
    company: state.company,
  };
};

const wrappedComponent = withRouter(connect(mapStateToProps)(RootRoute));

// Applied HOC here for DS so that it can be easily removed if ever we want
export default withDesignSystemLoader(wrappedComponent);



// WEBPACK FOOTER //
// ./src/routes.js